import { Link } from '@inertiajs/react'
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, CircleDashed, Database, ExternalLink, GitBranch } from 'lucide-react'
import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { FdeEditorPanel } from '@/frontend/features/Fde/FdeEditorSurface'
import { fdeProjectPath, fdeRoutes } from '@/frontend/features/Fde/routes'
import SemanticObjectPreview from '@/frontend/features/Fde/SemanticObjectPreview'
import type { FdeCopy, ProcessDataCoverage, ProcessDataCoverageState, ProcessNodeDefinition } from '@/frontend/features/Fde/types'

interface Props {
  projectId: string
  coverage: ProcessDataCoverage
  nodes: ProcessNodeDefinition[]
  copy: FdeCopy
  onSelectNode: (nodeId: string) => void
  onClose: () => void
}

const stateIcons = {
  confirmed: CheckCircle2,
  partial: CircleDashed,
  proposed: CircleDashed,
  missing: AlertTriangle,
}

const stateVariants = {
  confirmed: 'success',
  partial: 'warning',
  proposed: 'warning',
  missing: 'critical',
} as const

export default function ProcessDataCoveragePanel(props: Props) {
  const summary = props.coverage.summary

  return (
    <FdeEditorPanel
      title={props.copy.workspace.process_data_coverage}
      description={props.copy.workspace.process_data_coverage_description}
      icon={<Database />}
      closeLabel={props.copy.common.close}
      onClose={props.onClose}
      bodyClassName="space-y-5"
      testId="process-data-coverage"
    >
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-border pb-4 text-xs">
        <Summary label={props.copy.workspace.process_data_mapped} value={`${summary.mapped}/${summary.requirements}`} />
        <Summary label={props.copy.workspace.process_data_sources} value={summary.sources} />
        <Summary label={props.copy.workspace.process_data_missing} value={summary.missing} alert={summary.missing > 0} />
        <Button variant="outline" size="sm" className="ml-auto" asChild>
          <Link href={fdeProjectPath(fdeRoutes.data, props.projectId)}><ExternalLink />{props.copy.workspace.process_data_open_lineage}</Link>
        </Button>
      </div>

      {props.coverage.nodes.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border px-5 py-10 text-center">
          <Database className="mx-auto size-6 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">{props.copy.workspace.process_data_empty}</p>
        </div>
      ) : props.coverage.nodes.map((nodeCoverage) => {
        const node = props.nodes.find((candidate) => candidate.id === nodeCoverage.node_id)
        return (
          <section key={nodeCoverage.node_id} className="overflow-hidden rounded-xl border border-border bg-background">
            <button type="button" className="flex w-full items-center gap-2 border-b border-border bg-muted/20 px-4 py-3 text-left hover:bg-muted/40" onClick={() => props.onSelectNode(nodeCoverage.node_id)}>
              <GitBranch className="size-3.5 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate text-xs font-semibold">{node?.label || nodeCoverage.node_id}</span>
              <span className="text-[10px] text-muted-foreground">{nodeCoverage.requirements.length} {props.copy.workspace.process_data_requirements_short}</span>
            </button>
            <div className="divide-y divide-border">
              {nodeCoverage.requirements.map((requirement) => <RequirementRow key={requirement.requirement_key} projectId={props.projectId} requirement={requirement} copy={props.copy} />)}
            </div>
          </section>
        )
      })}
    </FdeEditorPanel>
  )
}

function RequirementRow({ projectId, requirement, copy }: { projectId: string; requirement: ProcessDataCoverage['nodes'][number]['requirements'][number]; copy: FdeCopy }) {
  const [expanded, setExpanded] = useState(false)
  const Icon = stateIcons[requirement.state]
  const propertyCoverage = requirement.available_properties > 0
    ? `${requirement.mapped_properties}/${requirement.available_properties}`
    : String(requirement.mapped_properties)

  return (
    <article className="px-4 py-3.5">
      <div className="flex min-w-0 items-start gap-3">
        <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-xs font-semibold text-foreground">{requirement.name}</h4>
            <Badge variant={stateVariants[requirement.state]} className="text-[9px]">{stateLabel(requirement.state, copy)}</Badge>
          </div>
          <p className="mt-1 text-[10px] text-muted-foreground">{requirement.ontology_class.name} · {copy.workspace[`process_role_${requirement.role}`]}</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {requirement.required_properties.length > 0 ? requirement.required_properties.map((property) => <span key={property.key} className="rounded-md border border-border bg-background px-2 py-1 text-[10px] text-foreground">{property.label}<span className={property.state === 'confirmed' ? 'ml-1 text-emerald-600' : property.state === 'proposed' ? 'ml-1 text-amber-600' : 'ml-1 text-destructive'}>·</span></span>) : <span className="rounded-md border border-border bg-background px-2 py-1 text-[10px] text-muted-foreground">{copy.workspace.process_data_identity_only}</span>}
          </div>
          {requirement.sources.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {requirement.sources.map((source) => <span key={source.id} className="rounded-md bg-muted px-2 py-1 text-[10px] text-foreground">{source.name}{source.grain ? ` · ${source.grain}` : ''}</span>)}
            </div>
          ) : <p className="mt-2 text-[10px] font-medium text-destructive">{copy.workspace.process_data_no_backing}</p>}
          <p className="mt-2 text-[10px] text-muted-foreground">{copy.workspace.process_data_properties}: {propertyCoverage}{!requirement.has_primary_key && requirement.state !== 'missing' ? ` · ${copy.workspace.process_data_identity_missing}` : ''}</p>
          {requirement.has_primary_key && requirement.state !== 'missing' && <button type="button" className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold text-primary hover:underline" onClick={() => setExpanded((current) => !current)}>{expanded ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}{expanded ? copy.workspace.semantic_objects_hide : copy.workspace.semantic_objects_show}</button>}
        </div>
      </div>
      {expanded && <div className="mt-3"><SemanticObjectPreview projectId={projectId} classId={requirement.ontology_class.id} copy={copy} limit={5} compact /></div>}
    </article>
  )
}

function Summary({ label, value, alert = false }: { label: string; value: string | number; alert?: boolean }) {
  return <span className="text-muted-foreground">{label} <strong className={alert ? 'text-destructive' : 'text-foreground'}>{value}</strong></span>
}

export function stateLabel(state: ProcessDataCoverageState, copy: FdeCopy) {
  return copy.workspace[`process_data_state_${state}`]
}

export { stateVariants }
